import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { CDL_CLASSES, TRAILER_TYPES, ENDORSEMENTS, SERVICE_RADIUS_OPTIONS, slugify } from "@/components/auth/signupConstants";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DriverMyListingSection = () => {
  const [profile, setProfile] = useState(undefined);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unlistConfirmOpen, setUnlistConfirmOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/drivers/profile/me`).then(({ data }) => {
      setProfile(data);
      if (data) {
        setForm({
          city: data.city || "", state: data.state || "", cdl_class: data.cdl_class || CDL_CLASSES[0],
          experience_years: data.experience_years ?? "", trailer_types: data.trailer_types || [],
          endorsements: data.endorsements || [], service_radius_miles: data.service_radius_miles || SERVICE_RADIUS_OPTIONS[0],
          listed: data.listed,
        });
      }
    }).catch(() => setProfile(null));
  }, []);

  const toggleArrayValue = (key, value) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value] }));

  const onListedChange = (checked) => {
    if (!checked) {
      setUnlistConfirmOpen(true);
      return;
    }
    setForm((f) => ({ ...f, listed: true }));
  };

  const confirmUnlist = () => {
    setForm((f) => ({ ...f, listed: false }));
    setUnlistConfirmOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/drivers/profile`, {
        name: profile.name, email: profile.email, phone: profile.phone,
        ...form, experience_years: Number(form.experience_years) || null, service_radius_miles: Number(form.service_radius_miles) || null,
      });
      toast.success(form.listed ? "Listing updated." : "You've been unlisted from the public directory.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update your listing.");
    } finally {
      setSaving(false);
    }
  };

  if (profile === undefined) return <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-rr-orange" /></div>;

  if (!profile) {
    return (
      <div data-testid="driver-no-listing-state" className="bg-white border border-rr-line rounded-2xl p-6 text-sm text-rr-muted">
        You haven't created a Driver Directory profile yet. <a href="/drivers" className="text-rr-orange font-bold hover:underline">Create one here</a> to get discovered by carriers and brokers.
      </div>
    );
  }

  return (
    <form onSubmit={save} data-testid="driver-my-listing-form" className="bg-white border border-rr-line rounded-2xl p-6 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="my-listing-city-input" placeholder="City" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
        <input data-testid="my-listing-state-input" placeholder="State" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className="border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <select data-testid="my-listing-cdl-select" value={form.cdl_class} onChange={(e) => setForm((f) => ({ ...f, cdl_class: e.target.value }))} className="border border-rr-line rounded-lg px-3 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
          {CDL_CLASSES.map((c) => (<option key={c} value={c}>CDL {c}</option>))}
        </select>
        <input type="number" data-testid="my-listing-experience-input" placeholder="Years experience" value={form.experience_years} onChange={(e) => setForm((f) => ({ ...f, experience_years: e.target.value }))} className="border border-rr-line rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-rr-orange" />
        <select data-testid="my-listing-radius-select" value={form.service_radius_miles} onChange={(e) => setForm((f) => ({ ...f, service_radius_miles: e.target.value }))} className="border border-rr-line rounded-lg px-3 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
          {SERVICE_RADIUS_OPTIONS.map((r) => (<option key={r} value={r}>Up to {r} mi</option>))}
        </select>
      </div>
      <div>
        <div className="text-xs font-semibold text-rr-muted mb-1.5">Trailer types</div>
        <div className="flex flex-wrap gap-2">
          {TRAILER_TYPES.map((t) => (
            <button type="button" key={t} data-testid={`my-listing-trailer-${slugify(t)}`} onClick={() => toggleArrayValue("trailer_types", t)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${form.trailer_types.includes(t) ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-rr-muted mb-1.5">Endorsements</div>
        <div className="flex flex-wrap gap-2">
          {ENDORSEMENTS.map((en) => (
            <button type="button" key={en} data-testid={`my-listing-endorsement-${slugify(en)}`} onClick={() => toggleArrayValue("endorsements", en)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${form.endorsements.includes(en) ? "bg-rr-orange text-white border-rr-orange" : "border-rr-line text-rr-muted"}`}>
              {en}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2.5 text-sm text-rr-muted cursor-pointer pt-1">
        <input type="checkbox" data-testid="my-listing-listed-checkbox" checked={form.listed} onChange={(e) => onListedChange(e.target.checked)} className="accent-rr-orange" />
        Keep my profile listed in the public RelayRigs driver directory.
      </label>
      <button type="submit" disabled={saving} data-testid="my-listing-save-button" className="bg-rr-orange text-white font-bold px-6 py-3 rounded-full hover:bg-rr-navy transition-colors flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={15} /> Save Changes</>}
      </button>

      <AlertDialog open={unlistConfirmOpen} onOpenChange={setUnlistConfirmOpen}>
        <AlertDialogContent data-testid="unlist-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unlist your profile?</AlertDialogTitle>
            <AlertDialogDescription>
              Carriers and brokers won't be able to find you in the public RelayRigs driver directory until you relist. You can turn this back on anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unlist-confirm-cancel-button">Keep Me Listed</AlertDialogCancel>
            <AlertDialogAction data-testid="unlist-confirm-button" onClick={confirmUnlist}>Yes, Unlist Me</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
};
